import { autocompletion, CompletionSource } from '@codemirror/autocomplete';
import { css } from '@codemirror/lang-css';
import { html } from '@codemirror/lang-html'; // Import the HTML language support
import { json } from '@codemirror/lang-json';
import { foldGutter } from '@codemirror/language';
import { lintGutter } from '@codemirror/lint';
import type { EditorState } from '@codemirror/state';
import { EditorView, ViewUpdate } from '@codemirror/view';
import { Box } from '@mantine/core';
import CodeMirror from '@uiw/react-codemirror';
import { csvHighlightPlugin, csvStyles } from '@widgets/jsonata/csvExtension';
import { decorationField } from '@widgets/jsonata/errorMarker';
import { jsonataCompleter } from '@widgets/jsonata/jsonataCompleter';
import { initializeJsonataEditor } from '@widgets/jsonata/jsonataMode';
import { memo, useRef } from 'react';
import './index.css';
import { jsonLinter } from './jsonExtension';

const customGutterTheme = EditorView.theme({
  '.cm-foldGutter': {
    width: '16px' // Gutter width
  },
  '.cm-foldGutter span': {
    fontSize: '12px', // Reduce caret size
    lineHeight: '1', // Fix vertical alignment
    padding: '0', // Remove any extra padding
    display: 'inline-block', // Standardize display
    textAlign: 'center' // Center align the caret
  }
});
const AppCodeEditor = memo(function AppCodeEditor(props: {
  id: string;
  readOnly?: boolean;
  mode?: 'json' | 'jsonata' | 'css' | 'plaintext' | 'html';
  value: string;
  setValue?: (value: string) => void;
  className?: string;
  onUpdate?: (viewUpdate: ViewUpdate) => void;
  onEditorReady?: (view: EditorView, state: EditorState) => void;
  width?: string | number;
  height?: string | number;
}) {
  const { value, setValue, id, readOnly, mode = 'json', className, onEditorReady } = props;

  const editorRef = useRef<(EditorView & { isInitialSetupDone: boolean }) | null>(null);

  // Custom function for initializing the editor
  const initExtensions = () => {
    const extensions = [
      EditorView.lineWrapping, // Enable line wrapping
      foldGutter({
        openText: '▼', // Open state icon
        closedText: '▶' // Closed state icon
      }),
      customGutterTheme
    ];

    // Apply syntax mode based on provided `mode` prop
    if (mode === 'json') {
      extensions.push(
        json(),
        lintGutter(), // Gutter for linting
        jsonLinter
      ); // Adds JSON linting);
    } else if (mode === 'jsonata') {
      extensions.push(
        initializeJsonataEditor(),
        autocompletion({
          override: [jsonataCompleter as unknown as CompletionSource]
        }),
        decorationField
      );
    } else if (mode === 'css') {
      extensions.push(css()); // Add CSS language support
    } else if (mode === 'html') {
      extensions.push(html()); // Add HTML language support
    } else if (mode === 'plaintext') {
      extensions.push(csvHighlightPlugin, csvStyles);
    }

    return extensions;
  };

  return (
    <Box
      className="editor-resizable"
      id={props.id}
      w={'100%'}
      h={'100%'}
      style={{
        overflowY: 'auto' /* Prevents overflow issues */
      }}
    >
      <CodeMirror
        id={id}
        style={{
          height: '100%'
        }}
        className={className}
        readOnly={readOnly}
        theme="light"
        value={value}
        onChange={setValue}
        ref={editorRef}
        onCreateEditor={(editor: EditorView, state: EditorState) => {
          // Set the initial value of the editor
          if (onEditorReady) {
            onEditorReady(editor, state); // Pass editor instance after first initialization
          }
        }}
        onUpdate={props.onUpdate && props.onUpdate}
        extensions={initExtensions()}
        basicSetup={{
          lineNumbers: true,
          highlightActiveLine: true,
          autocompletion: true,
          foldGutter: false
        }}
      />
    </Box>
  );
});

export default AppCodeEditor;
